import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { PageHead, PillView, useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { MedienAnsicht, type MedienKommentar } from "../../../components/medien/MedienAnsicht";
import { UploadFeld } from "../../../components/medien/UploadFeld";
import { TarifHinweis, tarifAnzeige, useTarifKatalog, useTarifStand, useUpgradeZiel } from "../../../components/Tarif";
import { naechsterTarif } from "../../../model/tarif";
import { STAND_TEXT, contentStand, groesse, speichernUnter, zeitmarke } from "../../../lib/medien";
import { ZIP_MAX_BYTES, makeZip } from "../../../lib/zip";
import { SPEICHER_HINWEIS_AB, speicherProzent } from "../../../lib/speicher";
import { currentLang, t, ta } from "../../../lib/sprache";
import { ListenVoreinstellungen } from "../einstellungen/Voreinstellungen";
import { fehlerText } from "../../../lib/fehler";
import { date } from "../../../lib/format";
import { pill, monthName } from "../kooperationen/model";
import { useKooperation } from "../kooperationen/hooks";
import { FreigabeMarke } from "../kooperationen/Marke";
import { kacheln, parseLibrary, passtFilter, teilbar, type Filter, type Kachel, type LibMedia, type LibSub, type Library } from "./model";
import "./content.css";

/**
 * Content-Ablage (0155): Ordner je Kooperation → Monat → Beitrag mit Vorschau, Version, Stand (Eingereicht, Korrektur,
 * Freigegeben, Bei der Marke, Von der Marke freigegeben), Filter und Suche. Ein Beitrag öffnet die Prüfung: Player mit
 * Kommentaren an Zeitmarken bzw. Punkten im Bild, Fassungen nebeneinander vergleichen, freigeben oder Korrektur anfordern
 * (Glocke und Mail an den Creator), mit der Marke teilen, einzeln oder als ZIP herunterladen, neue Dateien hochladen.
 */

const ABO = "/agentur/einstellungen/abo";

export function useContentLibrary() {
  const ws = useStaffWorkspace();
  return useQuery({ queryKey: ["content_library", ws.org.org_id], queryFn: async () => parseLibrary(await api.read("content_library", { p_org: ws.org.org_id })) });
}

/** Signierter Link zum Ansehen (kurzlebig, deshalb nach 10 Minuten neu holen) */
export function useMedienUrl(id: string | null | undefined) {
  // Links gelten 15 Minuten: nach 12 Minuten neu holen, damit Springen und Dateiwechsel auf einer offenen Seite weiter gehen
  return useQuery({ queryKey: ["medien-url", id ?? ""], enabled: !!id, staleTime: 10 * 60_000, gcTime: 12 * 60_000, refetchInterval: 12 * 60_000,
    queryFn: () => api.medien<{ url: string; poster: string | null }>({ action: "url", media: id! }) });
}

function useKommentare(id: string | null | undefined) {
  return useQuery({ queryKey: ["media_comments", id ?? ""], enabled: !!id,
    queryFn: () => api.select<{ id: string; time_ms: number | null; pos_x: number | null; pos_y: number | null; body: string; author_side: "staff" | "talent" | "brand";
      author_name: string | null; resolved_at: string | null; created_at: string }>("media_comments", { filters: [["media_id", "eq", id!]], order: [["created_at", "asc"]] }) });
}

const lang = () => currentLang();
const tt = (de: string, en: string) => t(de, en);
// Studio (Creator-Konto): Kommentare des Kontos stammen von dir, nicht von einem Team
const SEITEN = () => ({ staff: ta("Team", "Team", "Du", "You"), talent: "Creator", brand: t("Marke", "Brand") });

export function standPill(k: Pick<Kachel, "stand">) {
  if (k.stand === "leer") return pill(t("Noch nichts eingereicht", "Nothing submitted yet"), "neutral");
  const s = STAND_TEXT[k.stand];
  return pill(lang() === "en" ? s.en : s.de, s.tone);
}

export function Content() {
  const q = useContentLibrary();
  const [params, setParams] = useSearchParams();
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Loading />;
  const b = params.get("b");
  const dealId = params.get("k");
  const lib = q.data;
  if (b && dealId) {
    const deal = lib.deals.find((d) => d.id === dealId);
    const kachel = deal?.periods.flatMap((p) => kacheln(deal, p, monthName)).find((k) => k.key === b);
    if (kachel) return <Pruefen lib={lib} kachel={kachel} zurueck={() => setParams({})} />;
  }
  return <Ordner lib={lib} oeffnen={(k) => setParams({ k: k.deal.id, b: k.key })} />;
}

// ── Ordner ───────────────────────────────────────────────────────────────────────────────────────────────────────────────

function Ordner({ lib, oeffnen }: { lib: Library; oeffnen: (k: Kachel) => void }) {
  const ws = useStaffWorkspace();
  const [filter, setFilter] = useState<Filter>("alle");
  const [suche, setSuche] = useState("");
  const alle = useMemo(() => lib.deals.flatMap((d) => d.periods.flatMap((p) => kacheln(d, p, monthName))), [lib]);
  const gezeigt = alle.filter((k) => passtFilter(k, filter, suche));
  const zahl = (f: Filter) => alle.filter((k) => passtFilter(k, f, "")).length;
  const filters: { key: Filter; label: string }[] = [
    { key: "alle", label: t("Alle", "All") },
    ...(["eingereicht", "korrektur", "freigegeben", "bei_marke", "marke_frei"] as const).map((k) => ({ key: k as Filter, label: lang() === "en" ? STAND_TEXT[k].en : STAND_TEXT[k].de })),
    { key: "leer", label: t("Noch ohne Content", "No content yet") },
  ];
  return (
    <main id="main" className="content tk-content">
      <PageHead eyebrow={t("Kooperationen", "Deals")} title="Content"
        text={t("Videos, Bilder und PDF je Kooperation, Monat und Beitrag: prüfen, korrigieren, freigeben und mit der Marke teilen.",
          "Videos, images and PDFs per deal, month and post: review, correct, approve and share with the brand.")}>
        <ListenVoreinstellungen modul="content" />
      </PageHead>
      <Kontingent lib={lib} chef={ws.isChef} />
      <div className="toolbar co-content-toolbar">
        <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder={t("Kooperation, Creator, Marke, Beitrag …", "Deal, creator, brand, post …")}
          aria-label={t("Content durchsuchen", "Search content")} />
        <div className="co-filter" data-coco="content-filter" role="group" aria-label={t("Nach Stand filtern", "Filter by status")}>
          {filters.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key} className={filter === f.key ? "aktiv" : ""} onClick={() => setFilter(f.key)}>
              {f.label} <span className="co-zahl">{zahl(f.key)}</span>
            </button>
          ))}
        </div>
      </div>
      {!gezeigt.length && <p className="muted panel">{alle.length ? t("Nichts gefunden.", "Nothing found.") : t("Noch keine Beiträge mit Content. Sobald ein Creator etwas hochlädt oder ihr eine Fassung erfasst, erscheint sie hier.", "No posts with content yet. As soon as a creator uploads something or you record a version, it appears here.")}</p>}
      {lib.deals.map((d) => {
        const meine = gezeigt.filter((k) => k.deal.id === d.id);
        if (!meine.length) return null;
        const monate = [...new Set(meine.map((k) => k.period.id))];
        return (
          <details key={d.id} className="panel co-ordner" open>
            <summary>
              <span className="co-ordner-titel">{d.title}</span>
              <span className="muted">{d.talent}{d.brand ? ` · ${d.brand}` : ""}</span>
            </summary>
            {monate.map((pid) => {
              const p = d.periods.find((x) => x.id === pid)!;
              return (
                <section key={pid} className="co-monat" aria-label={monthName(p.month)}>
                  <h3>{monthName(p.month)}</h3>
                  <ul className="co-kacheln">
                    {meine.filter((k) => k.period.id === pid).map((k) => <KachelView key={k.key} k={k} onOpen={() => oeffnen(k)} />)}
                  </ul>
                </section>
              );
            })}
          </details>
        );
      })}
      {ws.isChef && <Aufbewahrung lib={lib} />}
    </main>
  );
}

function KachelView({ k, onOpen }: { k: Kachel; onOpen: () => void }) {
  const url = useMedienUrl(k.vorschau?.poster || k.vorschau?.kind === "image" ? k.vorschau.id : null);
  // Vorschaubild, wo es eines gibt (auch bei Bildern); sonst das Bild selbst
  const bild = url.data?.poster ?? (k.vorschau?.kind === "image" ? url.data?.url : null);
  return (
    <li>
      <button type="button" className="co-kachel" onClick={onOpen} aria-label={`${k.label} · ${standPill(k).label}`}>
        <span className="co-kachel-bild" data-art={k.vorschau?.kind ?? "leer"}>
          {bild ? <img src={bild} alt="" loading="lazy" /> : <span aria-hidden="true">{k.vorschau?.kind === "video" ? "▶" : k.vorschau?.kind === "pdf" ? "PDF" : k.letzte ? t("Link", "Link") : "＋"}</span>}
          {k.vorschau?.duration_ms ? <span className="co-dauer">{zeitmarke(k.vorschau.duration_ms)}</span> : null}
        </span>
        <span className="co-kachel-text">
          <strong>{k.label}</strong>
          <span className="row co-kachel-meta">
            {k.letzte && <span className="co-version">V{k.letzte.version}</span>}
            <PillView pill={standPill(k)} />
            {k.kommentare > 0 && <span className="co-kommentar-zahl" title={t("offene Kommentare", "open comments")}>💬 {k.kommentare}</span>}
            {k.neu.length > 0 && <span className="co-neu">{t(`${k.neu.length} neu`, `${k.neu.length} new`)}</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

function Kontingent({ lib, chef }: { lib: Library; chef: boolean }) {
  const ws = useStaffWorkspace();
  const usage = useQuery({ queryKey: ["storage", ws.org.org_id, "abo"], queryFn: () => api.read<{ bytes: number; limit_gb: number }>("org_storage_usage", { p_org: ws.org.org_id }).catch(() => null) });
  const l = lib.limits;
  const bytes = Number(usage.data?.bytes ?? 0);
  const pct = l.storage_gb > 0 ? speicherProzent(bytes, l.storage_gb) : 0;
  const videoPct = l.video_max > 0 ? Math.round((l.videos_used / l.video_max) * 100) : 0;
  const knapp = pct >= SPEICHER_HINWEIS_AB || videoPct >= SPEICHER_HINWEIS_AB;
  const upgrade = useUpgradeZiel();
  // K-583: Tarif und Ziel aus dem Katalog (Solo → Team, im Creator-Konto Creator → Creator Pro) statt fest „Solo“
  const stand = useTarifStand(l.video_max >= 0 ? ws.org.org_id : null);
  const katalog = useTarifKatalog();
  const planKey = stand.data?.plan ?? null;
  const planText = planKey ? tarifAnzeige(planKey, katalog.data) : null;
  const zielKey = naechsterTarif(planKey, { creator: ws.isCreator, plans: katalog.data?.plans ?? null });
  const zielText = zielKey ? tarifAnzeige(zielKey, katalog.data) : null;
  return (
    <section className={`co-kontingent row${knapp ? " notice" : ""}`} aria-label={t("Speicher der Content-Ablage", "Content storage")}>
      <span>{l.storage_gb > 0 ? t(`Speicher: ${groesse(bytes)} von ${l.storage_gb} GB (${pct} %)`, `Storage: ${groesse(bytes, "en")} of ${l.storage_gb} GB (${pct} %)`) : t("Speicher nach Vereinbarung", "Storage as agreed")}</span>
      {l.video_max >= 0 && <span>{t(`Videos: ${l.videos_used} von ${l.video_max} (${planText ? `Tarif ${planText} zum Kennenlernen` : "zum Kennenlernen"}, je bis ${l.video_mb} MB${zielText ? `; mehr Videos ab ${zielText}` : ""})`,
        `Videos: ${l.videos_used} of ${l.video_max} (${planText ? `${planText} plan to get started` : "to get started"}, up to ${l.video_mb} MB each${zielText ? `; more videos from ${zielText}` : ""})`)}</span>}
      {l.video_max < 0 && <span className="muted">{t(`Videos bis ${l.video_mb >= 2048 ? "2 GB" : `${l.video_mb} MB`} je Datei`, `Videos up to ${l.video_mb >= 2048 ? "2 GB" : `${l.video_mb} MB`} per file`)}</span>}
      {knapp && (chef ? <Link className="btn" to={upgrade?.to ?? ABO}>{upgrade?.label ?? t("Tarif ansehen", "View plan")}</Link> : <span>{t("Den Tarif wählt der Admin.", "The admin chooses the plan.")}</span>)}
    </section>
  );
}

function Aufbewahrung({ lib }: { lib: Library }) {
  const ws = useStaffWorkspace();
  const { busy, run } = useAction();
  const [tage, setTage] = useState(lib.keep_old_days == null ? "" : String(lib.keep_old_days));
  return (
    <details className="panel stack co-aufbewahrung">
      <summary>{t("Aufbewahrung und Datenschutz", "Retention and data protection")}</summary>
      <label>{t("Alte Fassungen löschen", "Delete old versions")}
        <select value={tage} onChange={(e) => setTage(e.target.value)}>
          <option value="">{t("Nie (alle Fassungen behalten)", "Never (keep all versions)")}</option>
          {[30, 90, 180, 365].map((d) => <option key={d} value={d}>{t(`${d} Tage nach Freigabe einer neueren Fassung`, `${d} days after a newer version was approved`)}</option>)}
        </select>
      </label>
      <div className="row">
        <button type="button" className="primary" disabled={busy !== null || tage === (lib.keep_old_days == null ? "" : String(lib.keep_old_days))}
          onClick={() => void run("aufbewahrung", () => api.update("org_settings", [["org_id", "eq", ws.org.org_id]], { media_keep_old_days: tage ? Number(tage) : null }, { returning: false }),
            t("Gespeichert.", "Saved."))}>{t("Speichern", "Save")}</button>
      </div>
      <p className="source-note">{t("Die neueste und jede freigegebene Fassung bleibt. Nach einer Kündigung werden alle Dateien der Content-Ablage mit den übrigen Daten gelöscht (Löschplan der Agentur). Videos und Bilder liegen verschlüsselt bei unserem Speicheranbieter in der EU (Unterauftragsverarbeiter laut AVV); Links sind persönlich und gelten nur Minuten.",
        "The newest and every approved version stay. After cancellation, all content files are deleted together with the other data (agency deletion plan). Videos and images are stored encrypted with our storage provider in the EU (sub-processor under the DPA); links are personal and valid for minutes only.")}</p>
    </details>
  );
}

// ── Prüfen ───────────────────────────────────────────────────────────────────────────────────────────────────────────────

function Pruefen({ lib, kachel: k, zurueck }: { lib: Library; kachel: Kachel; zurueck: () => void }) {
  const ws = useStaffWorkspace();
  const qc = useQueryClient();
  const { busy, error, run } = useAction();
  const koop = useKooperation(k.deal.id);
  // K-374: Upload-Grenze (Speicher) führt direkt zum Wechseldialog mit dem nächsten Tarif
  const upgrade = useUpgradeZiel();
  const [versionId, setVersionId] = useState<string | null>(k.letzte?.id ?? null);
  const [vergleich, setVergleich] = useState<string | null>(null);
  const [dateiIdx, setDateiIdx] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [korrektur, setKorrektur] = useState(false);
  // Prüf-Chat CP-015 (02.11.2026): nach „Korrektur senden“ blieb die Ansicht bis zum Neuladen auf „Eingereicht“ mit offenem
  // Formular. Die Entscheidung gilt sofort für diese Fassung (Rückmeldung oben), die Daten laden im Hintergrund neu.
  const [entschieden, setEntschieden] = useState<{ id: string; art: "frei" | "korrektur" } | null>(null);
  const [caption, setCaption] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const sub = k.fassungen.find((s) => s.id === versionId) ?? k.letzte;
  const dateien = (s: LibSub | null | undefined) => (s ? s.media_ids.map((id) => k.deal.media.find((m) => m.id === id)).filter((m): m is LibMedia => !!m) : []);
  const aktuelle = dateien(sub);
  const datei = aktuelle[Math.min(dateiIdx, Math.max(0, aktuelle.length - 1))] ?? null;
  const vglSub = vergleich ? k.fassungen.find((s) => s.id === vergleich) ?? null : null;
  const vglDatei = vglSub ? (dateien(vglSub).find((m) => m.kind === datei?.kind) ?? dateien(vglSub)[0] ?? null) : null;
  const schreiben = k.deal.can_write;
  const offen = sub?.status === "submitted" && sub === k.letzte && entschieden?.id !== sub.id;
  const neueFertig = k.neu.filter((m) => m.status === "ready");
  const neu = () => {
    void qc.refetchQueries({ queryKey: ["content_library"] });
    void qc.refetchQueries({ queryKey: ["deals", ws.org.org_id, "kooperation", k.deal.id] });
  };
  const koopSub = sub && koop.data ? koop.data.submissions.find((s) => s.id === sub.id) : undefined;
  const beitrag = k.item && koop.data ? koop.data.beitraege.find((b) => b.id === k.item!.id) : undefined;
  const zipSumme = aktuelle.reduce((s, m) => s + m.size, 0);
  const zip = async () => {
    setZipBusy(true);
    try {
      const files = [];
      for (const m of aktuelle) {
        const l = await api.medien<{ url: string }>({ action: "url", media: m.id, download: true });
        const res = await fetch(l.url);
        if (!res.ok) throw new Error(String(res.status));
        files.push({ name: m.name, data: await res.blob() });
      }
      const u = URL.createObjectURL(await makeZip(files));
      speichernUnter(u, `${k.deal.title}-${k.label}-V${sub?.version ?? 1}.zip`.replace(/[\\/:*?"<>|]+/g, "-"));
      setTimeout(() => URL.revokeObjectURL(u), 30_000);
    } catch {
      toast(t("Das ZIP-Paket ließ sich nicht erstellen. Bitte die Dateien einzeln herunterladen.", "The ZIP package could not be created. Please download the files one by one."), "alert");
    } finally {
      setZipBusy(false);
    }
  };
  return (
    <main id="main" className="content tk-content">
      <PageHead eyebrow={`${k.deal.title} · ${monthName(k.period.month)}`} title={k.label} text={`${k.deal.talent}${k.deal.brand ? ` · ${k.deal.brand}` : ""}`}>
        <button type="button" onClick={zurueck}>{t("Zurück zum Ordner", "Back to folder")}</button>
        <Link className="btn" to={`/agentur/kooperationen/${k.deal.id}`}>{t("Zur Kooperation", "Open deal")}</Link>
      </PageHead>
      {k.fassungen.length > 0 && (
        <div className="row co-fassungen" role="group" aria-label={t("Fassungen", "Versions")}>
          {k.fassungen.map((s) => (
            <button key={s.id} type="button" aria-pressed={s.id === sub?.id} className={s.id === sub?.id ? "aktiv" : ""} onClick={() => { setVersionId(s.id); setDateiIdx(0); }}>
              V{s.version} · <PillView pill={standPill({ stand: contentStand(s) })} />
            </button>
          ))}
          {k.fassungen.length > 1 && (
            <label className="co-vergleich-wahl">{t("Vergleichen mit", "Compare with")}
              <select value={vergleich ?? ""} onChange={(e) => setVergleich(e.target.value || null)}>
                <option value="">{t("—", "—")}</option>
                {k.fassungen.filter((s) => s.id !== sub?.id).map((s) => <option key={s.id} value={s.id}>V{s.version}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      {sub && (
        <section className="panel stack">
          {aktuelle.length > 1 && (
            <div className="co-dateireiter" role="tablist" aria-label={t("Dateien dieser Fassung", "Files of this version")}>
              {aktuelle.map((m, i) => <button key={m.id} type="button" role="tab" aria-selected={i === dateiIdx} className={i === dateiIdx ? "aktiv" : ""} onClick={() => setDateiIdx(i)}>{i + 1}. {m.name}</button>)}
            </div>
          )}
          {sub.caption && <p className="co-caption">{sub.caption}</p>}
          {sub.feedback && <div className="revision-note">{t("Rückmeldung", "Feedback")}: {sub.feedback}</div>}
          <div className={vglDatei ? "co-vergleich" : ""}>
            {vglDatei && vglSub && <Datei datei={vglDatei} titel={`V${vglSub.version}`} schreiben={false} />}
            {datei ? <Datei datei={datei} titel={vglDatei ? `V${sub.version}` : null} schreiben={schreiben} />
              : <p className="muted">{t("Diese Fassung hat keine Datei in der Ablage (Link oder Chat-Datei).", "This version has no file in the library (link or chat file).")}</p>}
          </div>
          {aktuelle.length > 0 && (
            <div className="row">
              {datei && <button type="button" onClick={() => void api.medien<{ url: string }>({ action: "url", media: datei.id, download: true }).then((l) => speichernUnter(l.url, datei.name))
                .catch((e: unknown) => toast(fehlerText(e), "alert"))}>{t("Herunterladen", "Download")}</button>}
              {aktuelle.length > 1 && (zipSumme <= ZIP_MAX_BYTES
                ? <button type="button" disabled={zipBusy} aria-busy={zipBusy} onClick={() => void zip()}>{t(`Alle als ZIP (${groesse(zipSumme)})`, `All as ZIP (${groesse(zipSumme, "en")})`)}</button>
                : <small className="muted">{t("Über 500 MB: bitte einzeln herunterladen.", "Over 500 MB: please download one by one.")}</small>)}
            </div>
          )}
          {entschieden && sub && entschieden.id === sub.id && sub.status === "submitted" && (
            <p className="notice" role="status">{entschieden.art === "korrektur"
              ? ta("Korrektur gesendet. Der Creator bekommt sie als Benachrichtigung und per E-Mail.", "Change request sent. The creator receives it as a notification and by email.", "Korrektur vermerkt.", "Change request recorded.")
              : t("Freigegeben.", "Approved.")}</p>
          )}
          {offen && schreiben && (
            <div className="stack co-entscheidung">
              {!korrektur ? (
                <div className="row">
                  <button type="button" className="primary" disabled={busy !== null}
                    onClick={() => void run("frei", () => api.rpc("content_review", { p_submission: sub.id, p_approve: true }), t("Freigegeben.", "Approved.")).then((r) => { if (r !== undefined) setEntschieden({ id: sub.id, art: "frei" }); neu(); })}>{t("Freigeben", "Approve")}</button>
                  <button type="button" onClick={() => setKorrektur(true)}>{t("Korrektur anfordern", "Request changes")}</button>
                </div>
              ) : (
                <form className="stack" onSubmit={(e) => {
                  e.preventDefault();
                  void run("korrektur", () => api.rpc("content_review", { p_submission: sub.id, p_approve: false, p_feedback: feedback.trim() }),
                    ta("Korrektur an den Creator geschickt (Glocke und Mail).", "Change request sent to the creator (notification and email).", "Korrektur vermerkt.", "Change request recorded.")).then((r) => { if (r !== undefined) { setKorrektur(false); setFeedback(""); setEntschieden({ id: sub.id, art: "korrektur" }); neu(); } });
                }}>
                  <label>{ta("Was soll sich ändern? (geht mit den Kommentaren an den Creator)", "What should change? (goes to the creator together with the comments)", "Was soll sich ändern? (Notiz zur nächsten Fassung)", "What should change? (note for the next version)")}
                    <textarea value={feedback} minLength={3} maxLength={4000} onChange={(e) => setFeedback(e.target.value)} required />
                  </label>
                  <div className="row">
                    <button type="submit" className="primary" disabled={busy !== null || feedback.trim().length < 3}>{t("Korrektur senden", "Send change request")}</button>
                    <button type="button" onClick={() => setKorrektur(false)}>{t("Abbrechen", "Cancel")}</button>
                  </div>
                </form>
              )}
              {error && <p className="tk-error" role="alert">{error}</p>}
            </div>
          )}
        </section>
      )}
      {koop.data && sub && (teilbar(k, sub) || sub.brand) && (
        <FreigabeMarke detail={koop.data} beitrag={beitrag} offen={teilbar(k, sub) ? koopSub : undefined} />
      )}
      {schreiben && (
        <section className="panel stack" aria-labelledby="co-hochladen">
          <h3 id="co-hochladen">{t("Neue Dateien", "New files")}</h3>
          <UploadFeld ziel={{ deal: k.deal.id, deliverable: k.item?.id ?? null, period: k.item ? null : k.period.id }} t={tt} lang={lang()} tarifLink={ws.isChef ? upgrade?.to ?? ABO : null} tarifLabel={upgrade?.label}
            videoMb={lib.limits.video_mb} onFertig={neu} />
          {neueFertig.length > 0 && (
            <form className="stack" onSubmit={(e) => {
              e.preventDefault();
              void run("einreichen", () => api.rpc("content_submit", { p_period: k.period.id, p_caption: caption.trim() || null, p_attachments: [], p_also_deal: false,
                p_deliverable: k.item?.id ?? null, p_media: neueFertig.map((m) => m.id) }), t("Als neue Fassung erfasst.", "Recorded as a new version.")).then((r) => { if (r !== undefined) { setCaption(""); neu(); } });
            }}>
              <ul className="co-neu-liste">{neueFertig.map((m) => <li key={m.id}>{m.name} · {groesse(m.size)}{m.uploaded_side === "talent" ? ` · ${t("vom Creator", "from the creator")}` : ""}</li>)}</ul>
              <label>{t("Caption oder Hinweis (optional)", "Caption or note (optional)")}<textarea value={caption} maxLength={4000} onChange={(e) => setCaption(e.target.value)} /></label>
              <div className="row">
                <button type="submit" className="primary" disabled={busy !== null || (k.letzte?.status === "submitted")}>{t(`Als Fassung V${(k.letzte?.version ?? 0) + 1} erfassen`, `Record as version V${(k.letzte?.version ?? 0) + 1}`)}</button>
                {k.letzte?.status === "submitted" && <small className="muted">{t("Erst die offene Fassung prüfen.", "Review the open version first.")}</small>}
              </div>
            </form>
          )}
        </section>
      )}
      <p className="source-note">{t(`Letzte Änderung ${sub ? date(sub.reviewed_at ?? sub.submitted_at) : "–"}. Jede Datei trägt ihren SHA-256; die Marke sieht nur die geteilte Fassung.`,
        `Last change ${sub ? date(sub.reviewed_at ?? sub.submitted_at) : "–"}. Every file carries its SHA-256; the brand only sees the shared version.`)}</p>
      {lib.limits.brand_review === false && !lib.limits.brand_review_trial && !sub?.brand && (
        <TarifHinweis modul="module_brand_review" was={t("Teilen mit der Marke", "Sharing with the brand")} />
      )}
    </main>
  );
}

function Datei({ datei, titel, schreiben }: { datei: LibMedia; titel: string | null; schreiben: boolean }) {
  const url = useMedienUrl(datei.id);
  const k = useKommentare(datei.id);
  const qc = useQueryClient();
  const kommentare: MedienKommentar[] = (k.data ?? []).map((c) => ({ id: c.id, time_ms: c.time_ms, x: c.pos_x, y: c.pos_y, body: c.body, side: c.author_side,
    name: c.author_name, resolved: !!c.resolved_at, at: c.created_at }));
  const neu = () => { void qc.invalidateQueries({ queryKey: ["media_comments", datei.id] }); void qc.invalidateQueries({ queryKey: ["content_library"] }); };
  return (
    <div className="stack">
      {titel && <strong>{titel}</strong>}
      {url.error ? <ErrorState error={url.error} retry={() => void url.refetch()} /> : (
        <MedienAnsicht datei={{ id: datei.id, name: datei.name, kind: datei.kind, size: datei.size, sha256: datei.sha256, duration_ms: datei.duration_ms, width: datei.width, height: datei.height }}
          url={url.data?.url ?? null} poster={url.data?.poster ?? null} kommentare={kommentare} t={tt} seiten={SEITEN()} kompakt={!!titel}
          onLadefehler={() => void url.refetch()}
          hinweis={ta("Der Creator sieht Kommentare des Teams sofort, die Marke nicht.", "The creator sees team comments right away; the brand does not.", "Die Marke sieht deine Kommentare nicht.", "The brand does not see your comments.")}
          onKommentar={schreiben ? async (c) => {
            try {
              await api.rpc("media_comment_add", { p_media: datei.id, p_body: c.body, p_time_ms: c.time_ms, p_x: c.x, p_y: c.y });
              neu();
              return true;
            } catch (e) {
              toast(fehlerText(e), "alert");
              return false;
            }
          } : undefined}
          onErledigt={schreiben ? (id, erledigt) => void api.rpc("media_comment_resolve", { p_comment: id, p_resolved: erledigt }).then(neu)
            .catch((e: unknown) => toast(fehlerText(e), "alert")) : undefined} />
      )}
    </div>
  );
}
